import React, { useState } from 'react';
import { AlertTriangle, X, Loader2 } from 'lucide-react';
import { Task } from '../types';
import { TaskApi } from '../api/taskApi';

interface DeprioritizeModalProps {
  task: Task;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export const DeprioritizeModal: React.FC<DeprioritizeModalProps> = ({
  task,
  onConfirm,
  onCancel,
}) => {
  const [reason, setReason] = useState('');
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = reason.trim().length >= 10 && !validating;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setValidating(true);
    setError(null);

    try {
      const { valid } = await TaskApi.validateReason(reason.trim());
      if (!valid) {
        setError("This doesn't look like a real reason. Please write a proper sentence explaining why you're moving this to backlog.");
        setValidating(false);
        return;
      }
    } catch {
      // If validation API fails, allow submission so the user isn't blocked
    }

    setValidating(false);
    onConfirm(reason.trim());
  };

  return (
    <div className='fixed inset-0 z-50 flex flex-col items-stretch justify-end sm:items-center sm:justify-center bg-black/40 backdrop-blur-sm sm:p-4'>
      <div className='bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-md'>
        {/* Header */}
        <div className='flex items-start justify-between px-4 sm:px-6 py-4 border-b border-gray-100'>
          <div className='flex items-center gap-3'>
            <div className='w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0'>
              <AlertTriangle size={18} className='text-amber-600' />
            </div>
            <div>
              <h2 className='text-sm font-semibold text-gray-900'>Move to Backlog</h2>
              <p className='text-xs text-gray-500 mt-0.5'>You must provide a reason</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className='p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors'
          >
            <X size={16} />
          </button>
        </div>

        {/* Task preview */}
        <div className='mx-4 sm:mx-6 mt-4 px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-200'>
          <p className='text-xs text-gray-500 font-medium mb-0.5'>{task.key}</p>
          <p className='text-sm text-gray-800 font-medium line-clamp-2'>{task.title}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className='px-4 sm:px-6 py-4 space-y-4'>
          <div>
            <label className='block text-xs font-semibold text-gray-700 mb-1.5'>
              Why are you deprioritizing this task?
            </label>
            <textarea
              value={reason}
              onChange={e => { setReason(e.target.value); setError(null); }}
              placeholder='e.g. Higher priority tasks came in. Will revisit after the release...'
              rows={4}
              autoFocus
              className={`w-full text-sm border rounded-lg px-3 py-2.5 outline-none focus:ring-2 transition-colors resize-none placeholder-gray-400 ${
                error
                  ? 'border-red-400 focus:border-red-400 focus:ring-red-50'
                  : 'border-gray-200 focus:border-amber-400 focus:ring-amber-50'
              }`}
            />
            {error ? (
              <p className='text-[11px] text-red-600 mt-1'>{error}</p>
            ) : (
              <p className='text-[11px] text-gray-400 mt-1'>
                {reason.trim().length < 10 ? 'Write a proper sentence explaining your reason' : 'Looks good — click below to confirm'}
              </p>
            )}
          </div>

          <div className='bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5'>
            <p className='text-xs text-amber-800'>
              This reason will be visible to the CEO and admins along with a system timestamp. You are accountable for this decision.
            </p>
          </div>

          <div className='flex justify-end gap-3 pt-1'>
            <button
              type='button'
              onClick={onCancel}
              disabled={validating}
              className='px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors font-medium disabled:opacity-40'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={!canSubmit}
              className='px-5 py-2 text-sm bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2'
            >
              {validating && <Loader2 size={13} className='animate-spin' />}
              {validating ? 'Checking…' : 'Move to Backlog'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
